import type { Prospect } from "../types";

// Pipeline stages — the prospect lifecycle (see db-design/01_prospects_schema.sql).
export const prospectStages = [
  { id: "Researching", name: "Researching" },
  { id: "Pre-Auction", name: "Pre-Auction" },
  { id: "Contacted", name: "Contacted" },
  { id: "Negotiating", name: "Negotiating" },
  { id: "Under Contract", name: "Under Contract" },
  { id: "Closed", name: "Closed" },
  { id: "Dead", name: "Dead" },
];

export const listingStatuses = [
  { id: "Active", name: "Active" },
  { id: "Expired", name: "Expired" },
  { id: "FSBO", name: "FSBO" },
  { id: "Withdrawn", name: "Withdrawn" },
  { id: "Terminated", name: "Terminated" },
  { id: "Agent-Owned", name: "Agent-Owned" },
];

export const auctionTypes = [
  { id: "foreclosure", name: "Foreclosure" },
  { id: "tax", name: "Tax" },
  { id: "trustee", name: "Trustee" },
];

export const auctionStatuses = [
  { id: "scheduled", name: "Scheduled" },
  { id: "postponed", name: "Postponed" },
  { id: "sold", name: "Sold" },
  { id: "canceled", name: "Canceled" },
];

export const occupancyTypes = [
  { id: "Owner Occupied", name: "Owner Occupied" },
  { id: "Absentee", name: "Absentee" },
];

export const contactPointTypes = [
  { id: "Work", name: "Work" },
  { id: "Home", name: "Home" },
  { id: "Other", name: "Other" },
];

export const defaultPhones = [{ number: null, type: "Work" }];
export const defaultEmails = [{ email: null, type: "Work" }];

const cleanProspectArrayFields = (data: Prospect) => {
  const cleanedPhones = (data.phones ?? []).filter((p) => p?.number != null);
  const cleanedEmails = (data.emails ?? []).filter((e) => e?.email != null);
  return {
    ...data,
    phones: cleanedPhones,
    emails: cleanedEmails,
  };
};

export const cleanupProspectForCreate = (data: Prospect) =>
  cleanProspectArrayFields(data);

export const cleanupProspectForEdit = (data: Prospect) =>
  cleanProspectArrayFields(data);

/** The primary phone number for list display (first entry). */
export const primaryPhone = (prospect: Prospect): string | undefined =>
  prospect.phones?.find((p) => p?.number)?.number ?? undefined;

// Numeric prospect columns — values arriving as query-param strings are coerced.
const PREFILL_NUMERIC = new Set([
  "beds",
  "baths",
  "sqft",
  "stories",
  "lot_acres",
  "year_built",
  "tax_value",
  "tax_amount",
  "tax_year",
  "avm",
  "list_price",
  "sold_amount",
  "original_mortgage",
  "estimated_equity",
  "days_on_market",
  "opening_bid",
  "min_bid",
  "lien_amount",
  "lien_position",
  "judgment_amount",
]);

// Text prospect columns accepted as prefill (must exist on the Prospect type).
const PREFILL_TEXT = new Set([
  "owner_name",
  "co_owner_name",
  "occupancy",
  "mailing_address",
  "mailing_city",
  "mailing_state",
  "mailing_zip",
  "property_address",
  "property_city",
  "property_state",
  "property_zip",
  "county",
  "subdivision",
  "parcel_apn",
  "legal_description",
  "property_type",
  "style",
  "listing_status",
  "list_agent",
  "list_office",
  "mls_id",
  "auction_type",
  "auction_status",
  "case_number",
  "plaintiff_lender",
  "trustee",
  "sale_location",
  "auction_date",
  "list_date",
  "sold_date",
  "redemption_deadline",
  "stage",
  "source",
  "source_record_id",
]);

/**
 * Build a prefill object for the create form from URL query params (the
 * "Send to CRM" deep link from the map tool). Param keys mirror Prospect
 * column names; `phone`/`email` collapse into the first phones/emails entry.
 * Unknown params and blank values are ignored.
 */
export const prefillFromParams = (
  params: URLSearchParams,
): Partial<Prospect> => {
  const out: Record<string, unknown> = {};

  for (const key of PREFILL_TEXT) {
    const v = params.get(key);
    if (v != null && v.trim() !== "") out[key] = v.trim();
  }
  for (const key of PREFILL_NUMERIC) {
    const v = params.get(key);
    if (v != null && v.trim() !== "") {
      const n = Number(v);
      if (Number.isFinite(n)) out[key] = n;
    }
  }

  const phone = params.get("phone");
  if (phone && phone.trim() !== "") {
    out.phones = [{ number: phone.trim(), type: "Work" }];
  }
  const email = params.get("email");
  if (email && email.trim() !== "") {
    out.emails = [{ email: email.trim(), type: "Work" }];
  }

  return out as Partial<Prospect>;
};
